import React, {
  type ComponentProps,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import {
  capitalizeFirstLetter,
  getSubstringsByPositions,
  HighlightWords,
  Icon,
  theme,
  usePressableState,
  webOnlyStyles,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationPrediction = (props: Props) => {
  const { prediction, iconName = 'IconCustomStrokeSm', onPress } = props;
  const { id, name, address, matchedSubstrings } = prediction;

  const { formatMessage } = useIntl();

  // ─── Refs ────────────────────────────────────────────────────────────

  const pressableRef = useRef(null);

  // ─── Derived Data ────────────────────────────────────────────────────

  const formattedAddress = useMemo(() => {
    if (!name) {
      return address;
    }

    const formattedName = capitalizeFirstLetter(name);

    if (address) {
      return formatMessage(messages.formattedAddress, { name: formattedName, address }); // prettier-ignore
    }

    return formattedName;
  }, [name, address, formatMessage]);

  const addressHighlights = useMemo(() => {
    if (!matchedSubstrings) {
      return [];
    }

    return getSubstringsByPositions(formattedAddress, matchedSubstrings);
  }, [formattedAddress, matchedSubstrings]);

  const a11yLabel = formatMessage(messages.selectAddressA11yLabel, { address: formattedAddress }); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress(id, prediction.address);
  }, [id, onPress, prediction.address]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const { isHovered, isActive } = usePressableState(pressableRef);

  const pressableStyles = [
    styles.pressable,
    isHovered ? styles.pressableHover : undefined,
    isActive ? styles.pressableActive : undefined,
    webStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={pressableRef}
      style={pressableStyles}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      onPress={handleOnPress}
    >
      <View>
        <Icon name={iconName} />
      </View>

      <View style={styles.pressableContent} pointerEvents="none">
        <HighlightWords
          text={formattedAddress}
          highlights={addressHighlights}
          selectable={false}
          numberOfLines={2}
        />
      </View>

      <View>
        <Icon name="IconCaretRight" width={16} height={16} />
      </View>
    </TouchableOpacity>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  formattedAddress: {
    defaultMessage: '{name} · {address}',
    description: 'Location Search | Predictions | Formatted address',
  },
  selectAddressA11yLabel: {
    defaultMessage: 'Selects "{address}" address',
    description: 'Location Search | Predictions | Selects address a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.radius.small,
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['3'],
  },
  pressableHover: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  pressableActive: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  pressableContent: {
    flex: 1,
  },
});

const webStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type Props = {
  prediction: Address;
  iconName?: ComponentProps<typeof Icon>['name'];
  onPress: (addressId: string, address: string) => void;
};

type Address = {
  id: string;
  name?: string;
  address: string;
  // @ts-expect-error TS(2503): Cannot find namespace 'google'.
  matchedSubstrings?: readonly google.maps.places.PredictionSubstring[];
};
